import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { getLastNDaysSaleQuery } from 'src/utils/eventQueryUtils';
import { CatalogClient, SaleClient, SaleQuery } from 'src/WebApiController';

import { useGetRecentSalesForEvents } from '../../Dashboard/DataInsightsChart/hooks/useGetRecentSalesForEvents';
import { useGetUnableToProcessSales } from '../../Dashboard/DataInsightsChart/hooks/useGetUnableToProcessSales';

export const PREVIOUS_DAYS_TO_PULL = 7;
const NEW_ROW_ACTIVE_DURATION = 5_000;

export const useRecentSalesSection = () => {
  const { activeAccountWebClientConfig } = useAppContext();

  const salesFilterQuery = getLastNDaysSaleQuery(PREVIOUS_DAYS_TO_PULL);
  const previousValues = useRef<Set<number>>();
  const [newRows, setNewRows] = useState<number[]>([]);

  const [activeTab, setActiveTab] = useState<
    'recentSales' | 'unableToProcessSales'
  >('recentSales');

  const handleTabChange = (tab: 'recentSales' | 'unableToProcessSales') => {
    setActiveTab(tab);
  };

  const { recentSalesData, isLoading: isRecentSalesLoading } =
    useGetRecentSalesForEvents(activeTab !== 'recentSales');

  const shouldQueryUnableToProcessSales = activeTab === 'unableToProcessSales';
  const {
    data: unprocessedSalesData,
    isLoading: isLoadingUnableToProcessSales,
    refetch: refetchUnableToProcessSales,
  } = useGetUnableToProcessSales(shouldQueryUnableToProcessSales);

  const flattenedData = useMemo(() => {
    return (
      (activeTab === 'recentSales' ? recentSalesData : unprocessedSalesData) ??
      []
    )
      .filter(
        (s) =>
          (activeTab === 'recentSales' && !s.isUnprocessedOrdFromPro) ||
          (activeTab === 'unableToProcessSales' && s.isUnprocessedOrdFromPro)
      )
      .toSorted(
        (a, b) =>
          new Date(b.saleDate).valueOf() - new Date(a.saleDate).valueOf()
      );
  }, [activeTab, recentSalesData, unprocessedSalesData]);

  useEffect(() => {
    if (!flattenedData.length) return;
    const isInitial = !previousValues.current;
    const prev = previousValues.current ?? new Set();
    const newValues = flattenedData
      .filter((sale) => !prev.has(sale.id))
      .map((sale) => sale.id);
    newValues.forEach((id) => prev.add(id));
    if (newValues.length && !isInitial) {
      setNewRows(newValues);
      setTimeout(() => {
        setNewRows([]);
      }, NEW_ROW_ACTIVE_DURATION);
    }
    previousValues.current = prev;
  }, [flattenedData]);

  const getCatalogDataExpanded = async () => ({});

  const getActivePosEntity = useCallback(
    async (saleId: number) => {
      const saleDetail = await new SaleClient(
        activeAccountWebClientConfig
      ).getSaleBySaleId(saleId);

      if (!saleDetail) return {};
      return {
        posEntityId: saleDetail.id,
        posEntity: saleDetail,
        posEntityDisplayId: saleDetail.idOnMkp,
      };
    },
    [activeAccountWebClientConfig]
  );

  return {
    salesFilterQuery,
    newRows,
    setNewRows,
    activeTab,
    handleTabChange,
    flattenedData,
    isLoading:
      activeTab === 'recentSales'
        ? isRecentSalesLoading
        : isLoadingUnableToProcessSales,
    refetchUnableToProcessSales,
    getCatalogDataExpanded,
    getActivePosEntity,
  };
};
